import {
  parentIncomingActions,
  VideoMakerOpener,
} from '@wix/video-maker-opener';
import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  FedopsInteractions,
  fedopsLogger,
} from '../../../services/fedopsLogger';
import { getAppInstance } from '../../getAppInstance';
import { noop } from '../../noop';

import { initializeVideoMakerOpenerParams } from './initializeVideoMakerOpenerParams';
import {
  IExtendedVideoMakerParams,
  IOuter,
  IVideoMakerVideoSavedToMediaManagerPayload,
  videoMakerLocale,
} from './useVideoMaker.types';

class VideoMaker extends VideoMakerOpener {
  constructor() {
    super();
  }

  offAll() {
    return noop();
  }
}

const VideoMakerContext = createContext<{
  videoMaker: VideoMakerOpener | null;
  openVideoMaker: (
    extendedVideoMakerParams?: IExtendedVideoMakerParams,
  ) => void;
}>({
  videoMaker: null,
  openVideoMaker: () => {},
});

export const VideoMakerProvider: FC<IOuter> = ({
  siteMediaToken,
  locale,
  children,
  metaSiteId,
  userId,
}) => {
  const videoMakerOpenSubscription = () => {
    fedopsLogger.interactionEnded(FedopsInteractions.VideoMakerModal);
  };

  const videoMaker = useMemo(() => new VideoMaker(), []);

  const openVideoMaker = useCallback(
    (extendedVideoMakerParams?: IExtendedVideoMakerParams) => {
      fedopsLogger.interactionStarted(FedopsInteractions.VideoMakerModal);
      void videoMaker.withInstanceGetter(getAppInstance).open(
        initializeVideoMakerOpenerParams({
          locale: locale as videoMakerLocale,
          metaSiteId,
          siteMediaToken,
          userId,
          extendedVideoMakerParams,
        }),
      );
    },
    [videoMaker, locale, metaSiteId, userId],
  );

  useEffect(() => {
    if (!videoMaker) {
      return;
    }

    videoMaker.on(
      parentIncomingActions.settingsReady,
      videoMakerOpenSubscription,
    );

    return () =>
      videoMaker.off(
        parentIncomingActions.settingsReady,
        videoMakerOpenSubscription,
      );
  }, [videoMaker, videoMakerOpenSubscription]);

  return (
    <VideoMakerContext.Provider
      value={{
        videoMaker,
        openVideoMaker,
      }}
    >
      {children}
    </VideoMakerContext.Provider>
  );
};

export const useVideoMaker = () => useContext(VideoMakerContext);

export const useVideoMakerFileSaved = (
  videoMakerFileSavedCallback: (
    payload: IVideoMakerVideoSavedToMediaManagerPayload,
  ) => void,
) => {
  const { videoMaker } = useVideoMaker();

  useEffect(() => {
    if (!videoMaker) {
      return;
    }

    const videoMakerFileSaveSubscription = ({
      payload,
    }: {
      payload: IVideoMakerVideoSavedToMediaManagerPayload;
    }): void => {
      videoMakerFileSavedCallback(payload);
    };

    videoMaker.on(
      parentIncomingActions.videoSavedToMediaManager,
      videoMakerFileSaveSubscription,
    );

    return () =>
      videoMaker.off(
        parentIncomingActions.videoSavedToMediaManager,
        videoMakerFileSaveSubscription,
      );
  }, [videoMaker, videoMakerFileSavedCallback]);
};
